import { MessageBlock } from '@deckee/deck-hand';
import React, { FC } from 'react';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import { type WeatherForecastDaySelectHandler } from '../../helpers/analytics/weatherForecastAnalytics';
import { FREE_FORECAST_DAYS } from '../../helpers/forecastHorizon';
import { type WeatherForecastDay } from '../../helpers/outlook/formatDays';
import RainCard from './RainCard';
import SwellCard from './SwellCard';
import TemperatureCard from './TemperatureCard/TemperatureCard';
import TideCard from './TideCard';
import UVCard from './UVCard';
import WindCard from './WindCard';
import { useWeatherForecastGraphData } from './useGraphData';

export interface GraphCardsProps {
  latitude: number;
  longitude: number;
  selectedDate: string;
  isToday: boolean;
  forecastDays?: WeatherForecastDay[];
  onSelectDay?: WeatherForecastDaySelectHandler;
  days?: number;
}

const GraphCards: FC<GraphCardsProps> = ({
  latitude,
  longitude,
  selectedDate,
  isToday,
  forecastDays = [],
  onSelectDay,
  days = FREE_FORECAST_DAYS,
}) => {
  const {
    windPoints,
    swellPoints,
    swellPeriodPoints,
    tidePoints,
    tideFooterText,
    rainfallPoints,
    temperaturePoints,
    uvPoints,
    location,
    isLoading,
    error,
  } = useWeatherForecastGraphData(latitude, longitude, days);

  const timeZone = location?.timeZone;

  if (error) {
    return (
      <MessageBlock type="error" size="small" mb={3}>
        {error}
      </MessageBlock>
    );
  }

  if (isLoading) {
    return (
      <>
        {['wind', 'swell', 'tide', 'rain', 'temperature', 'uv'].map(
          (cardKey) => (
            <Shimmer key={cardKey} on>
              <div
                data-testid={`weather-forecast-${cardKey}-card-loading`}
                style={{ minHeight: 220, marginBottom: 16 }}
              />
            </Shimmer>
          ),
        )}
      </>
    );
  }

  if (!timeZone) {
    return (
      <MessageBlock type="error" size="small" mb={3}>
        Unable to determine the local timezone for this forecast location.
      </MessageBlock>
    );
  }

  return (
    <>
      <WindCard
        windPoints={windPoints}
        selectedDate={selectedDate}
        timeZone={timeZone}
        isToday={isToday}
        latitude={latitude}
        longitude={longitude}
      />
      <SwellCard
        swellPoints={swellPoints}
        swellPeriodPoints={swellPeriodPoints}
        selectedDate={selectedDate}
        timeZone={timeZone}
        isToday={isToday}
        latitude={latitude}
        longitude={longitude}
        locationName={location?.name}
      />
      <TideCard
        tidePoints={tidePoints}
        footerText={tideFooterText}
        selectedDate={selectedDate}
        timeZone={timeZone}
        isToday={isToday}
        latitude={latitude}
        longitude={longitude}
        locationName={location?.name}
      />
      <RainCard
        rainfallPoints={rainfallPoints}
        selectedDate={selectedDate}
        timeZone={timeZone}
        latitude={latitude}
        longitude={longitude}
      />
      <TemperatureCard
        temperaturePoints={temperaturePoints}
        selectedDate={selectedDate}
        timeZone={timeZone}
        isToday={isToday}
        latitude={latitude}
        longitude={longitude}
        forecastDays={forecastDays}
        onSelectDay={onSelectDay}
      />
      <UVCard
        uvPoints={uvPoints}
        selectedDate={selectedDate}
        timeZone={timeZone}
        isToday={isToday}
        latitude={latitude}
        longitude={longitude}
      />
    </>
  );
};

export default GraphCards;
